import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Mail, X, Send, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Reusable Email Modal for emailing PO/Contract PDFs via Resend.
 *
 * Props:
 *  - open: boolean — whether modal is visible
 *  - onClose: () => void
 *  - title: string — modal heading e.g. "Email Purchase Order"
 *  - subtitle: string — e.g. "PO PO-1234 → ACME Vendors"
 *  - emailEndpoint: string — POST endpoint URL (relative to backend), e.g. `/stores/purchase-orders-v2/{id}/email`
 *  - statusEndpoint: string — GET endpoint for sender + sandbox info (defaults to `/projects/email-status`)
 *  - defaultTo: string — pre-fill recipient (the LATEST master-data email)
 *  - snapshotEmail: string — the email captured at PO-creation time. When present
 *      AND different from defaultTo, an inline warning surfaces both values
 *      so the user can decide which is correct.
 *  - defaultCc: string
 *  - defaultSubject: string
 *  - defaultBody: string — plain text; will be HTML-ified
 *  - onSent: () => void — called after a successful send so caller can refresh row badges
 */
export default function EmailModal({
  open,
  onClose,
  title = 'Email Document',
  subtitle = '',
  emailEndpoint,
  statusEndpoint = '/projects/email-status',
  defaultTo = '',
  snapshotEmail = '',
  defaultCc = '',
  defaultSubject = '',
  defaultBody = '',
  onSent = () => {},
}) {
  const { API } = useContext(AppContext);
  const [form, setForm] = useState({ to: '', cc: '', subject: '', body: '' });
  const [config, setConfig] = useState({ senderEmail: '', isSandbox: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setForm({
      to: defaultTo || '',
      cc: defaultCc || '',
      subject: defaultSubject || '',
      body: defaultBody || '',
    });
    axios
      .get(`${API}${statusEndpoint}`)
      .then((r) =>
        setConfig({
          senderEmail: r.data?.senderEmail || '',
          isSandbox: !!r.data?.isSandbox,
        }),
      )
      .catch(() => setConfig({ senderEmail: '', isSandbox: false }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.to.trim()) {
      setResult({ success: false, message: 'Recipient email is required.' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const htmlBody = `<div>${(form.body || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>')}</div>`;
      const resp = await axios.post(`${API}${emailEndpoint}`, {
        to: form.to,
        cc: form.cc,
        subject: form.subject,
        body: htmlBody,
      });
      setResult({
        success: true,
        message: `Email sent successfully${resp.data?.messageId ? ` (id: ${resp.data.messageId})` : ''}.`,
      });
      onSent();
    } catch (err) {
      setResult({
        success: false,
        message: err?.response?.data?.detail || err.message || 'Failed to send email.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="email-modal"
    >
      <form
        onSubmit={handleSend}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-teal-600 to-teal-700 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Mail size={18} /> {title}
            </h2>
            {subtitle && <p className="text-xs text-teal-100 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-white/80 hover:bg-white/10 rounded-lg"
            data-testid="email-modal-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div
            className={`border rounded-lg p-3 text-xs ${
              config.isSandbox
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-emerald-50 border-emerald-200 text-emerald-900'
            }`}
          >
            <strong>The PDF will be auto-attached.</strong>{' '}
            Sender: <code className="bg-white px-1 rounded">{config.senderEmail || 'loading…'}</code>.
            {config.isSandbox
              ? ' In Resend sandbox mode, delivery is restricted to verified addresses until a domain is verified.'
              : ' Verified sender — emails will deliver to any recipient.'}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase">To *</label>
            {(() => {
              const snap = (snapshotEmail || '').trim();
              const current = (form.to || '').trim();
              const showMismatch = snap && current && snap.toLowerCase() !== current.toLowerCase();
              if (!showMismatch) return null;
              return (
                <div
                  className="mt-1 mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                  data-testid="email-stale-snapshot-warning"
                >
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">Vendor email changed since this PO was created.</div>
                    <div className="mt-1 leading-relaxed">
                      Current master data:{' '}
                      <code className="bg-white px-1 py-0.5 rounded border border-amber-200">{current}</code>
                      <br />
                      PO snapshot (at creation):{' '}
                      <code className="bg-white px-1 py-0.5 rounded border border-amber-200">{snap}</code>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, to: snap }))}
                      className="mt-1.5 text-amber-800 underline hover:text-amber-900 font-medium"
                      data-testid="email-use-snapshot-btn"
                    >
                      Use PO snapshot instead
                    </button>
                  </div>
                </div>
              );
            })()}
            <input
              type="text"
              required
              value={form.to}
              onChange={(e) => setForm((p) => ({ ...p, to: e.target.value }))}
              placeholder="vendor@example.com (comma-separate for multiple)"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="email-to-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase">CC</label>
            <input
              type="text"
              value={form.cc}
              onChange={(e) => setForm((p) => ({ ...p, cc: e.target.value }))}
              placeholder="purchase@swiftstrips.com (comma-separate for multiple)"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="email-cc-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="email-subject-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase">Message</label>
            <textarea
              rows={8}
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              data-testid="email-body-input"
            />
          </div>
          {result && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                result.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-rose-50 border-rose-200 text-rose-800'
              }`}
              data-testid="email-result"
            >
              {result.message}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={sending || (result && result.success)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60"
            data-testid="email-send-btn"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? 'Sending…' : result && result.success ? 'Sent' : 'Send Email'}
          </button>
        </div>
      </form>
    </div>
  );
}
